import React, { useState, Fragment } from 'react';
import styled from 'styled-components';
import XLSX from 'xlsx';
import { filter, find, sort } from 'ramda';
import { COLOR } from '~~elements/Const';
import Input from '~~elements/Input';
import Button from '~~elements/Button';
import Select from '~~elements/Select';
import toast from '~~elements/Toast';
import ProgressBar from '~~elements/ProgressBar';
import { PlayerResource, GameResource, TeamResource } from '~~apis/resource';
import { SectionDiv } from '../styled';
import { exportGameBoxScoreExcel, exportTeamPbP } from '../service';
import BatchPbPUpload from './BatchPbPUpload';
import moment from 'moment';

const StyledDiv = styled.div`
  width: 100%;
  .admin-only {
    &--title {
      font-size: 1.1rem;
      margin: 0;
      padding: 0.5rem 0 0.2rem;
      border-bottom: 1px solid ${COLOR.secondary};
      display: inline-block;
    }
    &--content {
      width: 100%;
      display: block;
      overflow-x: auto;
      padding: 0.5rem 0;
    }
  }

  textarea {
    resize: none;
  }

  table {
    font-size: 0.9rem;
    border-collapse: collapse;
  }
  table,
  th,
  td {
    border: 1px solid #eee;
  }

  td {
    padding: 0.1rem 0.3rem;
  }
`;

const removeHideChar = (cell) => {
  return cell
    .replace('#', '')
    .replace(/(\r\n|\n|\r)/gm, '')
    .trim();
};

// 只有 Rebas 管理員才能使用
const AdminOnly = ({ tournament, teams, games }) => {
  const [numberMapping, setNumberMapping] = useState('-1');
  const [nameMapping, setNameMapping] = useState('-1');
  const [birthdayMapping, setBirthdayMapping] = useState('-1');
  const [rawRows, setRawRows] = useState([]);
  const [batchPlayerTeam, setBatchPlayerTeam] = useState('');

  const [batchBoxScoreDownload, setBatchBoxScoreDownload] = useState(false);
  const [batchBoxScoreTitle, setBatchBoxScoreTitle] = useState('讀取並轉換資料');
  const [batchBoxScoreCompleted, setBatchBoxScoreCompleted] = useState(0);

  const [batchPbPTeam, setBatchPbPTeam] = useState('');
  const [batchPbPDownload, setBatchPbPDownload] = useState(false);
  const [batchPbPTitle, setBatchPbPTitle] = useState('讀取並轉換資料');
  const [batchPbPCompleted, setBatchPbPCompleted] = useState(0);

  const handlePlayerExcelPaste = (e) => {
    e.preventDefault();

    setRawRows([]);
    navigator.clipboard.readText().then((clipText) => {
      const clipRows = clipText.split('\n');
      for (let i = 0; i < clipRows.length; i++) {
        clipRows[i] = clipRows[i].split('\t');
      }
      setRawRows(clipRows);
    });
  };

  const getColumnName = (columnIndex) => {
    const numberIndex = parseInt(numberMapping);
    const nameIndex = parseInt(nameMapping);
    const birthdayIndex = parseInt(birthdayMapping);
    switch (columnIndex) {
      case numberIndex:
        return '背號';
      case nameIndex:
        return '姓名';
      case birthdayIndex:
        return '生日';
      default:
        return columnIndex;
    }
  };

  const isImportable = (rawRow) => {
    const numberIndex = parseInt(numberMapping);
    const nameIndex = parseInt(nameMapping);
    if (Number.isNaN(numberIndex) || Number.isNaN(nameIndex)) {
      return false;
    }

    if (!rawRow[numberIndex] || !rawRow[numberIndex].replace('#', '').trim()) {
      return false;
    }

    if (!rawRow[nameIndex] || !rawRow[nameIndex].trim()) {
      return false;
    }

    return true;
  };

  const cancelPlayerExcelPaste = () => {
    setRawRows([]);
  };

  const handleBatchPlayerImport = () => {
    const numberIndex = parseInt(numberMapping);
    const nameIndex = parseInt(nameMapping);
    const birthdayIndex = parseInt(birthdayMapping);

    let rowCount = rawRows.length;
    const rowDone = () => {
      rowCount--;
      if (rowCount !== 0) {
        return;
      }

      toast('上傳成功！請至球隊列表確認！', { status: 'success', second: 2 });
      setRawRows([]);
      setBatchPlayerTeam('');
    };

    rawRows.forEach((rawRow) => {
      if (!isImportable(rawRow)) {
        rowDone();
        return;
      }

      const birthday = new Date(Number.isNaN(birthdayIndex) ? '' : removeHideChar(rawRow[birthdayIndex]));
      PlayerResource.createPlayerInSeasonTeamByOrganizer({
        leagueUniqid: tournament.league.uniqid,
        seasonUniqid: tournament.uniqid,
        seasonTeamUniqid: batchPlayerTeam,
        data: {
          number: removeHideChar(rawRow[numberIndex]),
          name: removeHideChar(rawRow[nameIndex]),
          birthday: moment(birthday).isValid() ? moment(birthday).format('YYYY/MM/DD') : '',
          player_uniqid: '',
        },
      })
        .catch((e) => {
          console.warn(e);
        })
        .finally(() => {
          rowDone();
        });
    });
  };

  const handleBatchBoxScoreClick = () => {
    setBatchBoxScoreDownload(true);
    setBatchBoxScoreCompleted(0);

    const hasBoxScoreGames = filter((g) => ['BOX', 'PBP'].includes(g.info.stats_completeness), games);
    setBatchBoxScoreTitle(`比賽數量： 0 / ${hasBoxScoreGames.length}`);

    const boxScoreResult = [];
    const writeExcel = () => {
      const title = `${tournament.title}-BoxScore成績-${moment().format('YYYYMMDD')}`;
      const wb = XLSX.utils.book_new();
      wb.Props = {
        Title: title,
        Author: 'Rebas 野革',
        CreatedDate: new Date(),
      };

      sort((a, b) => a.seq - b.seq, boxScoreResult).forEach((gameBoxScore) => {
        const awaySheetName = `G${gameBoxScore.seq}_AWAY_${gameBoxScore.awayTeam}`;
        const homeSheetName = `G${gameBoxScore.seq}_HOME_${gameBoxScore.homeTeam}`;
        wb.SheetNames.push(awaySheetName);
        wb.SheetNames.push(homeSheetName);

        const awaySheet = XLSX.utils.aoa_to_sheet(gameBoxScore.away);
        wb.Sheets[awaySheetName] = awaySheet;
        const homeSheet = XLSX.utils.aoa_to_sheet(gameBoxScore.home);
        wb.Sheets[homeSheetName] = homeSheet;
        return;
      });

      XLSX.writeFile(wb, `${title}.xlsx`);
    };

    let boxScoreDoneCount = 0;
    hasBoxScoreGames.forEach((game) => {
      GameResource.getGame({ seasonUniqid: tournament.uniqid, gameUniqid: game.uniqid })
        .then(({ data }) => {
          boxScoreResult.push({
            seq: game.seq,
            awayTeam: game.away.team,
            homeTeam: game.home.team,
            ...exportGameBoxScoreExcel(data, true),
          });
        })
        .catch(() => {
          toast(`G${game.seq} 無法取得資料，請稍後再試或嘗試單場下載`, { status: 'error', second: 5 });
        })
        .finally(() => {
          boxScoreDoneCount++;
          setBatchBoxScoreTitle(`比賽數量： ${boxScoreDoneCount} / ${hasBoxScoreGames.length}`);
          setBatchBoxScoreCompleted(((boxScoreDoneCount * 100) / hasBoxScoreGames.length).toFixed(1));
          if (boxScoreDoneCount < hasBoxScoreGames.length) {
            return;
          }
          writeExcel();
          setBatchBoxScoreDownload(false);
        });
      return;
    });
  };

  const handleBatchPbPClick = () => {
    const targetTeam = find((t) => t.uniqid === batchPbPTeam, teams);
    if (!targetTeam) {
      return;
    }
    setBatchPbPDownload(true);
    setBatchPbPCompleted(0);

    setBatchPbPTitle(`讀取 ${targetTeam.name} 資料`);

    TeamResource.getFirstBaseGamesInSeason({
      seasonUniqid: tournament.uniqid,
      seasonTeamUniqid: batchPbPTeam,
    })
      .then(({ data }) => {
        setBatchPbPTitle(`處理 ${targetTeam.name} 資料`);
        exportTeamPbP(targetTeam, data);
        setBatchPbPTitle(`下載 ${targetTeam.name} 資料`);
        setBatchPbPCompleted(100);
      })
      .catch((e) => {
        toast(`無法取得資料，請稍後再試`, { status: 'error', second: 5 });
      })
      .finally(() => {
        setBatchPbPDownload(false);
      });
  };

  return (
    <SectionDiv>
      <StyledDiv>
        <h4 className="admin-only--title">球員批次上傳</h4>
        <div className="admin-only--content">
          <i>球員背號跟球員名稱為必要項目，有生日才能與歷史資料連結。</i>
          <i>如背號跟現有的重複會直接被忽略。</i>
        </div>
        <div className="admin-only--content">
          {rawRows.length === 0 ? (
            <textarea onPaste={handlePlayerExcelPaste} rows="1" placeholder="複製 Excel 內容後貼上" />
          ) : (
            <Fragment>
              <p>
                <Button width="unset" color="transparentWithBorder" onClick={cancelPlayerExcelPaste}>
                  清空
                </Button>
              </p>
              <p>
                <label>
                  背號行號
                  <Input width="3rem" value={numberMapping} onChange={(e) => setNumberMapping(e.target.value)} />
                </label>
                <label>
                  姓名行號
                  <Input width="3rem" value={nameMapping} onChange={(e) => setNameMapping(e.target.value)} />
                </label>
                <label>
                  生日行號
                  <Input width="3rem" value={birthdayMapping} onChange={(e) => setBirthdayMapping(e.target.value)} />
                </label>
              </p>
              <table>
                <thead>
                  <tr>
                    <th />
                    {new Array(rawRows[0].length).fill('').map((_, columnIndex) => (
                      <th key={columnIndex}>{getColumnName(columnIndex)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rawRows.map((rawRow, rowIndex) => (
                    <tr key={rowIndex}>
                      <td>{!isImportable(rawRow) ? 'X' : 'O'}</td>
                      {rawRow.map((cellContent, columnIndex) => (
                        <td key={columnIndex}>{cellContent}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div>
                <Select label="匯入球隊" value={batchPlayerTeam} onChange={(e) => setBatchPlayerTeam(e.target.value)}>
                  <option value="">請選擇</option>
                  {teams.map((team) => (
                    <option key={team.uniqid} value={team.uniqid}>
                      {team.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Button color="red" disabled={!batchPlayerTeam} onClick={handleBatchPlayerImport}>
                  送出
                </Button>
              </div>
            </Fragment>
          )}
        </div>
        <h4 className="admin-only--title">比賽 Box Score Excel 批次下載</h4>
        <div className="admin-only--content">
          <i>如果要單場的請去個別比賽下載。</i>
        </div>
        <div className="admin-only--content">
          <i>批次下載已完賽且有 Box Score 的比賽。</i>
          <i>依照數量決定要等多久。</i>
        </div>
        <div className="admin-only--content">
          <Button width="10rem" color="red" disabled={batchBoxScoreDownload} onClick={handleBatchBoxScoreClick}>
            開始下載
          </Button>
        </div>
        <div
          className="admin-only--content"
          style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
        >
          <ProgressBar isOpen={true} title={batchBoxScoreTitle} completed={batchBoxScoreCompleted} />
        </div>
        <h4 className="admin-only--title">比賽 PbP Excel 批次下載</h4>
        <div className="admin-only--content">
          <i>批次下載已完賽且有 PbP 逐打席 的比賽。</i>
          <i>依照數量決定要等多久。</i>
        </div>
        <div className="admin-only--content">
          <Select value={batchPbPTeam} onChange={(e) => setBatchPbPTeam(e.target.value)}>
            <option value="">請選擇</option>
            {teams.map((team) => (
              <option key={team.uniqid} value={team.uniqid}>
                {team.name}
              </option>
            ))}
          </Select>
          <Button width="10rem" color="red" disabled={!batchPbPTeam || batchPbPDownload} onClick={handleBatchPbPClick}>
            開始下載
          </Button>
        </div>
        <div
          className="admin-only--content"
          style={{ background: 'linear-gradient(242deg, #094bcd, #28cfd7)', padding: '0.5rem' }}
        >
          <ProgressBar isOpen={true} title={batchPbPTitle} completed={batchPbPCompleted} />
        </div>
        <h4 className="admin-only--title">比賽 PbP Excel 批次上傳（限職業聯盟用）</h4>
        <div className="admin-only--content">
          <BatchPbPUpload teams={teams} games={games} />
        </div>
      </StyledDiv>
    </SectionDiv>
  );
};

export default AdminOnly;
